import type { ParseKeys } from 'i18next'

const SOURCE_ABILITIES = {
  salesforce: [
    { id: 'deals', askKey: 'connectors.abilities.salesforce.deals.ask', labelKey: 'connectors.abilities.salesforce.deals.label' },
    { id: 'accounts', askKey: 'connectors.abilities.salesforce.accounts.ask', labelKey: 'connectors.abilities.salesforce.accounts.label' },
  ],
  google_drive: [
    { id: 'recent', askKey: 'connectors.abilities.google_drive.recent.ask', labelKey: 'connectors.abilities.google_drive.recent.label' },
  ],
} as const
const SUMMARY_KEYS: Record<string, ParseKeys<'app'>> = {
  salesforce: 'connectors.abilities.salesforce.summary',
  google_drive: 'connectors.abilities.google_drive.summary',
  slack: 'connectors.summaries.slack',
  gmail: 'connectors.summaries.gmail',
  notion: 'connectors.summaries.notion',
  github: 'connectors.summaries.github',
  asana: 'connectors.summaries.asana',
  linear: 'connectors.summaries.linear',
  hubspot: 'connectors.summaries.hubspot',
  microsoft_teams: 'connectors.summaries.microsoft_teams',
  onedrive: 'connectors.summaries.onedrive',
  jira: 'connectors.summaries.jira',
  google_calendar: 'connectors.summaries.google_calendar',
  outlook: 'connectors.summaries.outlook',
  dropbox: 'connectors.summaries.dropbox',
  zoom: 'connectors.summaries.zoom',
  airtable: 'connectors.summaries.airtable',
  figma: 'connectors.summaries.figma',
  weather: 'connectors.summaries.weather',
}

export function sourceSlugKey(slug: string): string {
  return slug.trim().toLowerCase().replace(/-/g, '_')
}

/** Connection state is authoritative; names provide a stable order within each section. */
export function orderSources<T extends { slug: string; name: string; authenticated: boolean }>(connectors: Array<T>): Array<T> {
  return [...connectors].sort((left, right) =>
    Number(right.authenticated) - Number(left.authenticated) || left.name.localeCompare(right.name),
  )
}

/** Product copy is controlled independently of technical catalogue descriptions. */
export function integrationSummaryKey(slug: string): ParseKeys<'app'> {
  return SUMMARY_KEYS[sourceSlugKey(slug)] ?? 'connectors.integrationSummary'
}

export function sourceAbilities(slug: string) {
  const key = sourceSlugKey(slug)
  if (key === 'salesforce' || key === 'google_drive') return SOURCE_ABILITIES[key]
  return []
}

export function sourceAbilityKeys(slug: string): Array<string> {
  return sourceAbilities(slug).map(ability => ability.id)
}

export function sourceHasSummary(slug: string): boolean {
  return sourceAbilities(slug).length > 0
}

export function sourceHasHint(slug: string): boolean {
  return sourceSlugKey(slug) === 'google_drive'
}

/** Display an operation identifier without adding claims beyond the listed operation. */
export function capabilityLabel(name: string, slug: string): string {
  const withoutPrefix = name.startsWith(`${slug}_`) ? name.slice(slug.length + 1) : name
  const label = withoutPrefix.replace(/[_-]+/g, ' ').trim()
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : name
}
